import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function RiverTubing() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    { question: 'Is river tubing safe for children?', answer: 'Yes! Children aged 8 and older can participate with parental consent. The rapids are gentle (Grade 1-2), everyone wears life jackets, and safety kayakers stay close to help younger participants.' },
    { question: 'Do I need to know how to swim?', answer: 'Basic swimming ability is required for safety. While you will wear a life jacket throughout, you need to be comfortable in water in case you tip out of your tube.' },
    { question: 'Will I tip over in the rapids?', answer: 'Possibly! Tipping over is part of the fun and happens occasionally in the small rapids. The water is warm, you are wearing a life jacket, and your tube will not drift far.' },
    { question: 'Can I bring my phone?', answer: 'Only if it is in a completely waterproof case or dry bag securely attached to you. We recommend leaving valuables ashore.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Nile River Tubing
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Float, laugh, and splash through Uganda's gentle rapids
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(142 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-face-smile" className="w-4 h-4 mr-2" />
                Fun for All Ages
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-sun" className="w-4 h-4 mr-2" />
                Perfect Day Out
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-calendar-check" className="w-4 h-4 mr-2" />
                Free Cancellation
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Float
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                The Nile's Most Relaxing Adventure
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  River tubing in Jinja offers a sun-drenched afternoon floating down the legendary Nile River in a large inflatable tube, with cold drinks available and stunning scenery all around. This activity delivers a perfect blend of gentle adventure, social fun, and pure relaxation that requires zero special skills or extreme fitness. Unlike its high-octane cousin whitewater rafting, tubing takes participants through calmer sections where small rapids provide just enough splash and excitement to keep things interesting, while long peaceful stretches allow for conversation and soaking up the African sun.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your tubing adventure begins at a riverside base where you will be fitted with life jackets, helmets, and introduced to your trusty tube. After a brief safety talk, you will hop into your tube and let the current do most of the work. The route covers approximately 8-10 kilometers through diverse scenery: papyrus-lined channels where kingfishers dart, open sections with distant views of Jinja's hills, and shallow rapids where you will bounce and spin delightfully.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Most tubing trips include welcome breaks at riverside beaches where you can hop out, stretch your legs, enjoy provided snacks and drinks, and take group photos before continuing downstream. These beach stops transform tubing from a simple float into a proper social outing. The final stretch brings you back to civilization where transport awaits to return you to Jinja, usually arriving back sun-kissed, slightly waterlogged, and grinning from ear to ear.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Float With Us
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-face-smile', title: 'Easy & Fun', description: 'No experience needed—just hop in your tube and let the river do the work' },
                  { icon: 'fa-users', title: 'Perfect for Groups', description: 'Link tubes together, race friends, and create hilarious shared memories' },
                  { icon: 'fa-heart', title: 'Family-Friendly', description: 'Gentle rapids suitable for children 8+ and multi-generational adventures' },
                  { icon: 'fa-umbrella-beach', title: 'Beach Breaks Included', description: 'Stop at riverside beaches for refreshments, photos, and swimming' },
                  { icon: 'fa-shield-halved', title: 'Safety Support', description: 'Guides in kayaks accompany every float, providing assistance when needed' },
                  { icon: 'fa-droplet', title: 'Refreshing Rapids', description: 'Grade 1-2 waves provide splashy excitement without intense challenge' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Tubing Adventure Timeline
              </h2>
              <div className="space-y-6">
                {[
                  { time: '12:00', title: 'Base Check-In', desc: 'Arrive at launch site, register, get fitted with life jacket and helmet.' },
                  { time: '12:15', title: 'Safety Brief', desc: 'Quick orientation on tubing basics, river features, and safety protocols.' },
                  { time: '12:30', title: 'Launch & Float', desc: 'Enter river in your tube and begin floating through scenic calm sections.' },
                  { time: '13:15', title: 'First Rapids', desc: 'Navigate fun Grade 1-2 waves with splashes and spinning—guides assist.' },
                  { time: '14:00', title: 'Beach Break', desc: 'Stop at riverside beach for swimming, snacks, drinks, and group photos.' },
                  { time: '14:45', title: 'Continue Downstream', desc: 'More gentle rapids and calm floating through beautiful Nile scenery.' },
                  { time: '16:00', title: 'Finish & Transport', desc: 'Exit river, return equipment, board transport back to Jinja base.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Swimsuit and towel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Light shirt for sun protection</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Water shoes or old sneakers</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                    Essentials
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof sunscreen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sunglasses with strap</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof camera case (optional)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Frequently Asked Questions" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Jinja Water Fun
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/kayaking-adventures" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800"
                      alt="Kayaking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Kayaking Adventures</h3>
                    <p className="text-gray-700 mb-4">Paddle the Nile at your own pace.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/whitewater-rafting" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=800"
                      alt="Whitewater Rafting"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Whitewater Rafting</h3>
                    <p className="text-gray-700 mb-4">Grade 5 rapids for adrenaline seekers.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 3-4 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Easy</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Small groups</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 8+ years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Float
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Inflatable tube",
                    "Life jacket & helmet",
                    "Safety kayak escorts",
                    "Beach refreshments",
                    "Transport to/from river"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Hotel pickup/drop-off",
                    "Personal insurance",
                    "Towel and dry clothes",
                    "Tips for guides"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Swimming:</strong> Basic ability required.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Gear:</strong> Life jackets mandatory.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Nile River Tubing"
      />
    </div>
  )
}
